import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../api';

export default function SubjectTopicsSection({ token, setError }) {
  const { subjectId } = useParams();
  const navigate = useNavigate();

  const [subject, setSubject] = useState(null);
  const [topics, setTopics] = useState(null);
  const [loading, setLoading] = useState(true);

  const [topicModalOpen, setTopicModalOpen] = useState(false);
  const [topicModalMode, setTopicModalMode] = useState('add');
  const [topicModalData, setTopicModalData] = useState({ name: '' });

  const fetchSubject = async () => {
    try {
      const res = await api.get('/admin/subjects', { headers: { Authorization: `Bearer ${token}` } });
      const subj = (res.data.subjects || []).find(s => String(s.id) === String(subjectId));
      setSubject(subj);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load subject');
    }
  };

  const fetchTopics = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/admin/subjects/${subjectId}/topics`, { headers: { Authorization: `Bearer ${token}` } });
      setTopics(res.data.topics);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load topics');
      setTopics([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!token) return;
    fetchSubject();
    fetchTopics();
  }, [token, subjectId]);

  if (!token) return null;

  return (
    <div className="mb-6">
      <div className="sticky top-0 z-10 bg-white/80 backdrop-blur border-b border-gray-100 py-3 px-1 rounded-t-lg flex items-center justify-between mb-4 shadow-sm">
        <div>
          <h3 className="text-2xl font-bold text-indigo-700 tracking-tight">Topics for {subject?.name ?? '...' }</h3>
          <p className="text-sm text-gray-500">Topics are grouped under a subject and can be assigned to questions.</p>
        </div>
        <div className="flex gap-2">
          <button
            className="bg-gray-200 text-gray-700 px-3 py-1 rounded hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-300"
            onClick={() => navigate('/subjects')}
            aria-label="Back to Subjects"
          >
            Back to Subjects
          </button>
          <button
            className="bg-emerald-500 hover:bg-emerald-600 text-white font-semibold px-4 py-2 rounded-lg shadow focus:outline-none focus:ring-2 focus:ring-emerald-300 transition"
            onClick={() => {
              setTopicModalMode('add');
              setTopicModalData({ name: '' });
              setTopicModalOpen(true);
            }}
            aria-label="Add Topic"
          >
            + Add Topic
          </button>
        </div>
      </div>

      {loading ? (
        <div className="py-12 text-center text-gray-500 bg-white rounded-lg shadow-inner mt-8 animate-pulse">Loading topics…</div>
      ) : topics && topics.length === 0 ? (
        <div className="py-16 text-center text-gray-500 bg-white rounded-lg shadow-inner mt-8">
          <div className="text-2xl mb-2">No topics found</div>
          <div className="mb-4">Get started by adding your first topic.</div>
          <button
            className="bg-emerald-500 hover:bg-emerald-600 text-white font-semibold px-4 py-2 rounded-lg shadow focus:outline-none focus:ring-2 focus:ring-emerald-300 transition"
            onClick={() => {
              setTopicModalMode('add');
              setTopicModalData({ name: '' });
              setTopicModalOpen(true);
            }}
            aria-label="Add Topic"
          >
            + Add Topic
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {topics.map((t) => (
            <div key={t.id} className="group p-4 bg-white rounded-xl border border-slate-200 shadow hover:shadow-xl hover:-translate-y-1 transition-all duration-200 flex justify-between items-start focus-within:ring-2 focus-within:ring-indigo-300 outline-none" tabIndex={0} aria-label={`Topic ${t.name}`}>
              <div>
                <div className="font-medium text-slate-800 text-lg truncate flex items-center gap-2">
                  <span className="inline-block w-2 h-2 rounded-full bg-indigo-400" />
                  {t.name}
                </div>
                <div className="text-xs text-gray-500 mt-1">Created: {new Date(t.created_at).toLocaleDateString()}</div>
              </div>
              <div className="flex flex-col gap-2 flex-shrink-0 items-end">
                <button
                  className="text-blue-500 hover:text-blue-700 text-sm font-medium px-2 py-1 rounded focus:outline-none focus:ring-2 focus:ring-blue-200"
                  onClick={() => {
                    setTopicModalMode('edit');
                    setTopicModalData({ id: t.id, name: t.name });
                    setTopicModalOpen(true);
                  }}
                  aria-label={`Edit topic ${t.name}`}
                >
                  Edit
                </button>
                <button
                  className="text-red-500 hover:text-red-700 text-sm font-medium px-2 py-1 rounded focus:outline-none focus:ring-2 focus:ring-red-200"
                  onClick={async () => {
                    try {
                      await api.delete(`/admin/topics/${t.id}`, { headers: { Authorization: `Bearer ${token}` } });
                      fetchTopics();
                    } catch (e) {
                      setError(e.response?.data?.error || '');
                    }
                  }}
                  aria-label={`Delete topic ${t.name}`}
                >
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {topicModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50 animate-fade-in">
          <div className="bg-white p-6 rounded-xl shadow-2xl w-full max-w-md border border-gray-100">
            <h4 className="text-lg font-semibold mb-2">{topicModalMode === 'edit' ? 'Edit' : 'Add'} Topic</h4>
            <div className="space-y-2">
              <input
                placeholder="Topic name"
                value={topicModalData?.name || ''}
                onChange={(e) => setTopicModalData({ ...topicModalData, name: e.target.value })}
                className="border rounded px-2 w-full focus:ring-2 focus:ring-indigo-200"
                aria-label="Topic name"
              />
            </div>
            <div className="flex justify-end space-x-2 mt-4">
              <button className="px-3 py-1 rounded bg-gray-200 hover:bg-gray-300" onClick={() => setTopicModalOpen(false)}>Cancel</button>
              <button
                className="px-3 py-1 rounded bg-blue-600 text-white hover:bg-blue-700"
                onClick={async () => {
                  try {
                    if (topicModalMode === 'edit') {
                      await api.put(`/admin/topics/${topicModalData.id}`, { name: topicModalData.name }, { headers: { Authorization: `Bearer ${token}` } });
                    } else {
                      await api.post(`/admin/subjects/${subjectId}/topics`, { name: topicModalData.name }, { headers: { Authorization: `Bearer ${token}` } });
                    }
                    fetchTopics();
                  } catch (e) {
                    setError(e.response?.data?.error || '');
                  }
                  setTopicModalOpen(false);
                }}
                aria-label={topicModalMode === 'edit' ? 'Update topic' : 'Add topic'}
              >
                {topicModalMode === 'edit' ? 'Update' : 'Add'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
